import React from 'react';
import { Box, CircularProgress, Stack, Tooltip, Typography } from '@mui/material';
import {
  Error as ErrorIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { AppButton } from '../design-system';
import { useSubscriptionNotification } from '../../hooks/useSubscriptionNotification';
import type { BillingNotificationResponse } from '@cnapse/shared/schemas.js';

const animatedIconSx = {
  '@keyframes subscriptionNoticeIconPulse': {
    '0%, 100%': { opacity: 1, transform: 'scale(1)' },
    '50%': { opacity: 0.72, transform: 'scale(1.1)' },
  },
  animation: 'subscriptionNoticeIconPulse 2.6s ease-in-out infinite',
};

function severityIcon(severity: BillingNotificationResponse['severity']): React.ReactElement {
  const animate = severity === 'warning' || severity === 'error';
  const iconSx = { fontSize: 18, ...(animate ? animatedIconSx : {}) };

  switch (severity) {
    case 'error':
      return <ErrorIcon sx={iconSx} />;
    case 'warning':
      return <WarningIcon sx={iconSx} />;
    default:
      return <InfoIcon sx={iconSx} />;
  }
}

function severityColor(severity: BillingNotificationResponse['severity']): string {
  switch (severity) {
    case 'error':
      return 'error.main';
    case 'warning':
      return 'warning.dark';
    default:
      return 'info.main';
  }
}

/**
 * Compact subscription notice for the global app toolbar.
 */
export const SubscriptionToolbarNotice: React.FC = () => {
  const {
    notification,
    loading,
    toolbarCompactLine,
    toolbarDetailLine,
    showActionButton,
    openingPortal,
    actionError,
    handleAction,
  } = useSubscriptionNotification();

  if (loading || !notification?.show || !toolbarCompactLine || !toolbarDetailLine) {
    return null;
  }

  const color = severityColor(notification.severity);

  const tooltipContent = (
    <Stack spacing={0.75} sx={{ maxWidth: 360, py: 0.25 }}>
      <Typography variant="body2" sx={{ lineHeight: 1.45 }}>
        {toolbarDetailLine}
      </Typography>
      {actionError ? (
        <Typography variant="caption" color="error.light">
          {actionError}
        </Typography>
      ) : null}
      {showActionButton ? (
        <AppButton
          size="small"
          variant="outlined"
          color="inherit"
          onClick={(event) => {
            event.stopPropagation();
            void handleAction();
          }}
          disabled={openingPortal}
          startIcon={openingPortal ? <CircularProgress size={12} color="inherit" /> : undefined}
          sx={{
            alignSelf: 'flex-start',
            borderColor: 'rgba(255,255,255,0.4)',
            color: 'inherit',
            mt: 0.25,
          }}
        >
          {openingPortal ? 'Opening…' : notification.actionLabel || 'Take action'}
        </AppButton>
      ) : null}
    </Stack>
  );

  return (
    <Tooltip
      title={tooltipContent}
      enterDelay={200}
      slotProps={{
        tooltip: {
          sx: { bgcolor: 'grey.900', color: 'common.white', px: 1.5, py: 1 },
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.5,
          minWidth: 0,
          maxWidth: '100%',
          overflow: 'hidden',
          bgcolor: 'transparent',
          cursor: 'default',
        }}
      >
        <Box sx={{ color, display: 'flex', flexShrink: 0 }}>{severityIcon(notification.severity)}</Box>
        <Typography
          variant="body2"
          noWrap
          sx={{
            minWidth: 0,
            color,
            fontSize: '0.9375rem',
            lineHeight: 1.3,
            fontWeight: 600,
            letterSpacing: '0.01em',
          }}
        >
          {toolbarCompactLine}
        </Typography>
      </Box>
    </Tooltip>
  );
};

export default SubscriptionToolbarNotice;
